import { phoneNational } from "../lib/format";
import type { SignAsk, SignDoc, SignParty } from "../types";
import { IcoClose, IcoCopy, IcoPlus, IcoSend, IcoTrash } from "./icons";

export function SendSheet({
  doc,
  parties,
  asks,
  link,
  mailed,
  failed,
  waiting,
  busy,
  err,
  onPartyEmail,
  onPartyPhone,
  onPartyAuth,
  onAskLabel,
  onAskReq,
  onAskAdd,
  onAskDel,
  senderName,
  onSenderName,
  onSend,
  onCopy,
  onClose,
}: {
  doc: SignDoc;
  parties: SignParty[];
  asks: SignAsk[];
  link: string;
  mailed: string[];
  failed: { email: string; error?: string | null }[];
  waiting: string[];
  busy: boolean;
  err: string;
  onPartyEmail: (id: number, email: string) => void;
  onPartyPhone: (id: number, phone: string) => void;
  onPartyAuth: (id: number, key: "auth_mail" | "auth_sms" | "otp_exempt", on: boolean) => void;
  onAskLabel: (i: number, label: string) => void;
  onAskReq: (i: number, on: boolean) => void;
  onAskAdd: () => void;
  onAskDel: (i: number) => void;
  senderName: string;
  onSenderName: (v: string) => void;
  onSend: () => void;
  onCopy: () => void;
  onClose: () => void;
}) {
  const canSend = parties.some((p) => (p.email || "").trim().length >= 5) && senderName.trim().length >= 2;
  const who = senderName.trim() || "Votre société";
  const done = mailed.length > 0 || (link !== "" && failed.length > 0);

  return (
    <div className="sg-send-back" role="dialog" aria-label="Envoyer à signer">
      <div className="sg-send is-wide">
        <h2>{done ? "Envoi" : "Envoyer à signer"}</h2>
        <p style={{ color: "var(--sg-muted)", margin: "0.2rem 0 0" }}>{doc.title}</p>
        {err ? <p className="sg-err">{err}</p> : null}
        {!done ? (
          <>
            <p className="sg-legal">
              La première partie reçoit l’invitation. La suivante n’est contactée qu’après sa signature. Un code
              d’accès est obligatoire, sauf circuit interne explicite. Le code part à part (jamais dans le même mail
              que le lien).
            </p>
            <label>
              Nom vu par le destinataire
              <input
                className="sg-input"
                type="text"
                value={senderName}
                autoComplete="organization"
                placeholder="M. Dupont, votre service comptabilité…"
                onChange={(e) => onSenderName(e.target.value)}
              />
            </label>
            <p className="sg-send__preview">
              <strong>{who}</strong> vous demande de signer « {doc.title} »
            </p>
            <ul className="sg-send__parties">
              {parties.map((p, i) => (
                <li key={p.id}>
                  <em style={{ background: p.color }} aria-hidden="true" />
                  <div>
                    <strong>
                      {p.name}
                      {i === 0 ? " · première" : ` · ensuite`}
                    </strong>
                    <input
                      type="email"
                      value={p.email || ""}
                      autoComplete="email"
                      placeholder="prenom.nom@entreprise.fr"
                      onChange={(e) => onPartyEmail(p.id, e.target.value)}
                    />
                    {p.auth_sms && !p.otp_exempt ? (
                      <input
                        type="tel"
                        value={phoneNational(p.phone || "")}
                        autoComplete="tel"
                        placeholder="06 12 34 56 78"
                        onChange={(e) => onPartyPhone(p.id, e.target.value)}
                      />
                    ) : null}
                    <div className="sg-send__auth">
                      <span>Code</span>
                      <label className="sg-send__sw">
                        <span>E-mail</span>
                        <button
                          type="button"
                          role="switch"
                          className={`sg-authsw${p.auth_mail && !p.otp_exempt ? " is-on" : ""}`}
                          aria-checked={!!p.auth_mail && !p.otp_exempt}
                          disabled={!!p.otp_exempt}
                          onClick={() => onPartyAuth(p.id, "auth_mail", !p.auth_mail)}
                        >
                          <i className="sg-authsw__knob" aria-hidden="true" />
                        </button>
                      </label>
                      <label className="sg-send__sw">
                        <span>SMS</span>
                        <button
                          type="button"
                          role="switch"
                          className={`sg-authsw${p.auth_sms && !p.otp_exempt ? " is-on" : ""}`}
                          aria-checked={!!p.auth_sms && !p.otp_exempt}
                          disabled={!!p.otp_exempt}
                          onClick={() => onPartyAuth(p.id, "auth_sms", !p.auth_sms)}
                        >
                          <i className="sg-authsw__knob" aria-hidden="true" />
                        </button>
                      </label>
                    </div>
                    <label className="sg-send__sw sg-send__internal">
                      <span>Circuit interne — sans code</span>
                      <button
                        type="button"
                        role="switch"
                        className={`sg-authsw${p.otp_exempt ? " is-on" : ""}`}
                        aria-checked={!!p.otp_exempt}
                        onClick={() => onPartyAuth(p.id, "otp_exempt", !p.otp_exempt)}
                      >
                        <i className="sg-authsw__knob" aria-hidden="true" />
                      </button>
                    </label>
                  </div>
                </li>
              ))}
            </ul>
            <div className="sg-send__asks">
              <p>Pièces à joindre</p>
              {asks.map((a, i) => (
                <div key={i} className="sg-send__ask">
                  <input value={a.label} placeholder="Pièce d’identité, IBAN…" onChange={(e) => onAskLabel(i, e.target.value)} />
                  <button
                    type="button"
                    role="switch"
                    className={`sg-reqsw${a.required ? " is-on" : ""}`}
                    aria-checked={a.required}
                    title={a.required ? "Obligatoire" : "Facultatif"}
                    onClick={() => onAskReq(i, !a.required)}
                  >
                    <i className="sg-reqsw__knob" aria-hidden="true" />
                  </button>
                  <button type="button" className="sg-iconbtn is-del" aria-label="Retirer" onClick={() => onAskDel(i)}>
                    <IcoTrash />
                  </button>
                </div>
              ))}
              <button type="button" className="sg-btn ghost" onClick={onAskAdd}>
                <IcoPlus />
                Ajouter une pièce
              </button>
            </div>
          </>
        ) : (
          <>
            {mailed.length > 0 ? (
              <p className="sg-toast" style={{ margin: "0.9rem 0 0" }}>
                Invitation envoyée à {mailed.join(", ")}
                {waiting.length > 0 ? ` — ensuite ${waiting.join(", ")}.` : "."}
              </p>
            ) : null}
            {failed.length > 0 ? (
              <p className="sg-err">
                Pas parti : {failed.map((f) => f.email).join(", ")}. Vous pouvez copier le lien.
              </p>
            ) : null}
          </>
        )}
        <div className="sg-send__actions">
          {!done ? (
            <button type="button" className="sg-btn" disabled={busy || !canSend} onClick={onSend}>
              <IcoSend />
              {busy ? "Envoi…" : "Envoyer à la première partie"}
            </button>
          ) : (
            <button type="button" className="sg-btn" onClick={onCopy} disabled={!link}>
              <IcoCopy />
              Copier le lien
            </button>
          )}
          <button type="button" className="sg-btn ghost" onClick={onClose}>
            <IcoClose />
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
